import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Stepper, Step } from 'react-form-stepper';
import RequestService from '../../services/requestService';
import MessengerService from '../../services/messengerService';
import { GetDataEmail } from '../../components/utils/data-storage';
import { showSuccessDetail, showAdminCancelDetail, showConfirm, showError, showLoading, hideLoading } from '../../components/utils/swal-helpers';
import { useUserRequestForm } from '../user/user-request-form';
import { isDocumentDelivery } from '../user/constants';
import UserInfoSection from '../user/sections/user-info-section';
import DocumentDeliverySection from '../user/sections/document-delivery-section';
import BankingSection from '../user/sections/banking-section';
import TransactionSection from '../user/sections/transaction-section';
import FormDatePicker from '../../components/common/form-date-picker';
import useHolidays from '../../hooks/use-holidays';
import ReturnModal from '../../components/common/return-modal';
import AssignModal from '../../components/common/assign-modal';
import DoneWithConditionModal from '../../components/common/done-with-condition-modal';
import StopBadge from '../../components/common/step-badge';
import { WF } from '../../components/utils/wf-constants';
import { printDeliverySlip } from '../../components/utils/print-delivery-slip';
import MailService from '../../services/mailService';

// ─────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────

/** Workflow statuses ที่แสดงปุ่ม Print */
const WF_SHOW_PRINT  = [WF.ASSIGN, WF.REASSIGN, WF.RETURN, WF.INCOMPLETE, WF.COMPLETE, WF.CLOSE_OK, WF.CLOSE_COND, WF.ADMIN_CANCEL];

/** Workflow statuses ที่แสดงปุ่ม Cancel */
const WF_SHOW_CANCEL = [WF.CREATE, WF.ASSIGN, WF.DOING, WF.REASSIGN, WF.RETURN, WF.USER_RESPONDED];

/** Workflow statuses ที่ F.values.admin_remark จาก hook ไม่ได้ map note ให้ ต้อง fetch เองตรงๆ */
const NOTE_FETCH_STATUSES = [WF.ADMIN_CANCEL, WF.INCOMPLETE];

// ── workaround ชั่วคราว: BE available_only ยังไม่กรอง login_start_date ที่ยังไม่ถึง ──
const isMessengerReadyNow = (m) => {
    if (m.is_outsource !== 1) return true; // internal ไม่มีวันจำกัด ถือว่าพร้อมเสมอ
    const now = new Date();
    const start = m.login_start_date ? new Date(m.login_start_date) : null;
    const end   = m.login_end_date   ? new Date(m.login_end_date)   : null;
    if (start && now < start) return false; // ยังไม่ถึงวันเริ่มงาน
    if (end   && now > end)   return false; // เลยวันหมดอายุแล้ว
    return true;
};

const DELIVERY_TYPE_OPTIONS = [
    { value: 1, label: 'รับเท่านั้น' },
    { value: 2, label: 'ส่งเท่านั้น' },
    { value: 3, label: 'ส่งและรับกลับ' },
];

const DOCUMENT_SECRET_OPTIONS = [
    { value: 1, label: 'ปกติ (Normal)' },
    { value: 2, label: 'ลับเฉพาะ (Confidential)' },
];

const COMPLETION_TIME_OPTIONS = [
    { value: 1, label: 'เสร็จวันนี้เท่านั้น (Complete today)' },
    { value: 2, label: 'ระบุเวลา (Set a specific time)' },
];

// ─────────────────────────────────────────────────────────
// Shared sub-components
// ─────────────────────────────────────────────────────────

/** Radio group แบบ disabled (read-only สำหรับ admin view) */
const RadioGroup = ({ name, options, value }) => (
    <div className="radio-group">
        {options.map((opt) => (
            <label className="radio-option" key={opt.value}>
                <input
                    type="radio" name={name} value={opt.value}
                    checked={value === opt.value} disabled onChange={() => {}}
                />
                {opt.label}
            </label>
        ))}
    </div>
);

/** แสดงข้อมูล messenger ที่ได้รับมอบหมาย */
const MessengerSection = ({ messenger }) => {
    if (!messenger) return null;
    return (
        <div className="messenger-section">
            <div className="sub-section-label" style={{ marginTop: 16 }}>
                Messenger (พนักงานที่ส่งเอกสาร)
            </div>
            <div className="form-row">
                <div className="form-group">
                    <label className="form-label">ชื่อ-สกุล (Name-Surname)</label>
                    <input className="form-control" value={`${messenger.name} ${messenger.surname}`} disabled />
                </div>
                <div className="form-group">
                    <label className="form-label">เบอร์ติดต่อ (Tel.)</label>
                    <input className="form-control" value={messenger.tel} disabled />
                </div>
            </div>
        </div>
    );
};

/** Accordion card สำหรับ request destination แต่ละจุด */
const AccordionCard = ({ title, refNo, isOpen, onToggle, badge, children }) => (
    <div className={`accordion-card${isOpen ? ' accordion-card--open' : ''}`}>
        <div className="accordion-card__header" onClick={onToggle}>
            <div className="accordion-card__header-left">
                <div className="accordion-card__title-group">
                    <div className="accordion-card__title-row">
                        <i className={`accordion-card__chevron ${isOpen ? 'ri-arrow-up-s-fill' : 'ri-arrow-down-s-fill'}`} />
                        <div className="accordion-card__title">{title}</div>
                    </div>
                    {refNo && <div className="accordion-card__refno">Ref No. {refNo}</div>}
                </div>
            </div>
            {badge && <div className="accordion-card__badge">{badge}</div>}
        </div>
        {isOpen && <div className="accordion-card__body">{children}</div>}
    </div>
);

// ─────────────────────────────────────────────────────────
// Status Banner renderer
// ─────────────────────────────────────────────────────────

/**
 * แสดง status badge + remark box ตาม workflow status
 * แยกออกมาเพื่อลด if-chain ใน main component
 */
const StatusBanner = ({ wf, adminRemark, requestDetails }) => {
    const isMultiStop = wf === WF.DOING && requestDetails.length > 1;

    if ([WF.ASSIGN, WF.REASSIGN].includes(wf)) return (
        <span className="detail-status-badge detail-status-badge--inprogress">
            <i className="ri-feedback-fill" /> In progress
        </span>
    );

    if (wf === WF.DOING && requestDetails.length <= 1) return (
        <span className="detail-status-badge detail-status-badge--indelivery">
            <i className="ri-feedback-fill" /> In Delivery
        </span>
    );

    if (isMultiStop) return (
        <Stepper
            activeStep={-1}
            styleConfig={{
                activeBgColor: '#e2e8f0', activeTextColor: 'transparent',
                completedBgColor: '#22C55E', completedTextColor: '#fff',
                inactiveBgColor: '#e2e8f0', inactiveTextColor: 'transparent',
                size: '2.5em', circleFontSize: '0', borderRadius: '50%', fontWeight: '600',
            }}
            connectorStateColors={true}
            connectorStyleConfig={{ completedColor: '#22C55E', activeColor: '#e2e8f0', disabledColor: '#e2e8f0', size: 2 }}
        >
            {requestDetails.map((d, i) => (
                <Step key={i} completed={d.is_complete == 1} label="" />
            ))}
        </Stepper>
    );

    if (wf === WF.INCOMPLETE) return (
        <>
            <span className="detail-status-badge detail-status-badge--failed">
                <i className="ri-feedback-fill" /> Failed
            </span>
            {adminRemark && (
                <div className="admin-remark-box admin-remark-box--failed">
                    <div className="admin-remark-box__label">เหตุผล</div>
                    <div className="admin-remark-box__text">{adminRemark}</div>
                </div>
            )}
        </>
    );

    if ([WF.RETURN, WF.USER_RESPONDED].includes(wf)) return (
        <>
            <span className="detail-status-badge detail-status-badge--return">
                <i className="ri-feedback-fill" />
                {wf === WF.RETURN ? 'Return to User' : 'Responded by user'}
            </span>
            {adminRemark && (
                <div className={`admin-remark-box${wf === WF.USER_RESPONDED ? ' admin-remark-box--responded' : ''}`}>
                    <div className="admin-remark-box__label">สิ่งที่ให้แก้ไข</div>
                    <div className="admin-remark-box__text">{adminRemark}</div>
                </div>
            )}
        </>
    );

    if (wf === WF.COMPLETE) return (
        <span className="detail-status-badge detail-status-badge--delivered">
            <i className="mdi mdi-check-circle" /> Delivered
        </span>
    );

    if ([WF.CLOSE_OK, WF.CLOSE_COND].includes(wf)) return (
        <>
            <span className="detail-status-badge detail-status-badge--completed">
                <i className="ri-checkbox-circle-fill" />
                {wf === WF.CLOSE_OK ? 'Completed' : 'Done with Condition'}
            </span>
            {wf === WF.CLOSE_COND && adminRemark && (
                <div className="admin-remark-box admin-remark-box--condition">
                    <div className="admin-remark-box__label">เหตุผล</div>
                    <div className="admin-remark-box__text">{adminRemark}</div>
                </div>
            )}
        </>
    );

    if (wf === WF.ADMIN_CANCEL) return (
        <>
            <span className="detail-status-badge detail-status-badge--cancel">
                <i className="ri-feedback-fill" /> Cancel
            </span>
            {adminRemark && (
                <div className="admin-remark-box admin-remark-box--cancel">
                    <div className="admin-remark-box__label">เหตุผลการยกเลิก</div>
                    <div className="admin-remark-box__text">{adminRemark}</div>
                </div>
            )}
        </>
    );

    return null;
};

// ─────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────

const AdminRequestDetail = () => {
    const navigate = useNavigate();
    const { id }   = useParams();

    const {
        dataUser, formIsDisabled,
        pickupLocations, serviceTypes,
        providers, providerBranches, loadingProviders, loadingBranches,
        destProviders, destProviderBranches, loadingDestProviders, loadingDestBranches,
        buildings, provinces, districts, loadingDistricts,
        refNo,
        validation: F, fProps,
        destinations, MAX_EXTRA_DESTINATIONS,
        transactionProviders, loadingTransactionProviders,
        transactionBranches, loadingTransactionBranches, requestDetails,
    } = useUserRequestForm();

    const [senderInfo, setSenderInfo]                       = useState(null);
    const [messengers, setMessengers]                       = useState([]);
    const [showAssign, setShowAssign]                       = useState(false);
    const [showReturn, setShowReturn]                       = useState(false);
    const [showCancel, setShowCancel]                       = useState(false);
    const [showManageJob, setShowManageJob]                 = useState(false);
    const [showDoneWithCondition, setShowDoneWithCondition] = useState(false);
    const [openCards, setOpenCards]                         = useState({ main: true });
    const [statusNote, setStatusNote]                       = useState('');

    const { isDateAvailable } = useHolidays();
    const manageJobRef = useRef(null);

    const wf               = F.values.id_workflow_status;
    const isMultiStopDelivery = (wf === WF.DOING || wf === WF.INCOMPLETE) && requestDetails.length > 1;

    // console.log('F.values.messenger:', F.values.messenger);
    // console.log('F.values.id_messenger:', F.values.id_messenger);

    useEffect(() => {
        loadMessengers();
    }, []);

    useEffect(() => {
        if (!F.values.id || F.values.id === 0) return;
        loadSenderInfo(F.values.id);
    }, [F.values.id]);

    // ── โหลด note ของ workflow แถวปัจจุบัน มาแสดงใน StatusBanner ──
    // ── เฉพาะ status ที่ F.values.admin_remark จาก hook ไม่ได้ map ให้ (ADMIN_CANCEL, INCOMPLETE) ──
    useEffect(() => {
        if (!F.values.id || !NOTE_FETCH_STATUSES.includes(wf)) {
            setStatusNote('');
            return;
        }
        loadStatusNote(F.values.id, wf);
    }, [F.values.id, wf]);

    // ปิด manage job dropdown เมื่อคลิกนอก
    useEffect(() => {
        const handler = (e) => {
            if (manageJobRef.current && !manageJobRef.current.contains(e.target)) {
                setShowManageJob(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    // ─────────────────────────────────────────────────────
    // Data loaders
    // ─────────────────────────────────────────────────────

    const loadMessengers = async () => {
        try {
            const resp = await MessengerService.SearchMessenger({ status: 1, available_only: true });
            if (!resp.data.is_success || !resp.data.result) return;
            // ── workaround: กรองซ้ำฝั่ง FE เพราะ BE available_only ยังมีบั๊ก (ดู issue login_start_date) ──
            setMessengers(resp.data.result.filter(isMessengerReadyNow));
        } catch (err) { console.error('loadMessengers error:', err); }
    };

    const loadSenderInfo = async (requestId) => {
        try {
            const res = await RequestService.GetRequestById(requestId);
            const source = res.data.result;
            if (source?.sender_user) {
                setSenderInfo({
                    ...source.sender_user,
                    tel: source.sender_user.tel || source.sender_tel || "",
                });
            }
        } catch (err) { console.error('loadSenderInfo error:', err); }
    };

    const loadStatusNote = async (requestId, wfStatus) => {
        try {
            const resp = await RequestService.SearchRequestWorkflows({
                id_request: requestId,
                id_workflow_status: wfStatus,
            });
            if (resp.data.is_success && resp.data.result?.length > 0) {
                const latest = resp.data.result[resp.data.result.length - 1];
                setStatusNote(latest.note || '');
            }
        } catch (err) { console.error('loadStatusNote error:', err); }
    };

    // ─────────────────────────────────────────────────────
    // Handlers
    // ─────────────────────────────────────────────────────

    const handlePrint = async () => {
        try {
            showLoading();
            await printDeliverySlip(F.values, destinations, refNo);
        } finally {
            hideLoading();
        }
    };

    const handleCancel = async (note) => {
        setShowCancel(false);
        try {
            showLoading();
            await RequestService.ChangeWorkflowStatus(F.values.id, {
                id_workflow_status: WF.ADMIN_CANCEL,
                note,
                update_by: GetDataEmail(),
            });
            hideLoading();
            try {
                await MailService.SendMailByTemplate(
                    {
                        id_workflow_status: WF.ADMIN_CANCEL,
                        id_request: F.values.id,
                    }
                );
            } catch (err) {
                console.error('send email error:', err.response?.data);
            }
            setShowCancel(false);
            await showAdminCancelDetail({ title: 'เปลี่ยนสถานะเป็น Cancel', detail: 'ปิดคำขอสำเร็จแล้ว' });
            navigate('/admin/requests', { replace: true });
        } catch (err) { hideLoading(); showError(); }
    };

    const handleReturn = async (note) => {
        setShowReturn(false);
        const confirmed = await showConfirm({
            title: 'ยืนยันการส่งคำขอกลับให้ User',
            detail: 'กด Confirm เพื่อดำเนินการต่อ',
        });
        if (!confirmed.isConfirmed) return;
        try {
            showLoading();
            const resp = await RequestService.ChangeWorkflowStatus(F.values.id, {
                id_workflow_status: WF.RETURN,
                note,
                update_by: GetDataEmail(),
            });
            hideLoading();
            // ─── เช็ค status เปลี่ยนได้ไหม แทนที่จะเช็ค is_success ───
            if (resp.data.is_success) {
                await showSuccessDetail({
                    title: 'ส่งกลับไปยัง User แล้ว',
                    detail: 'หาก user ทำการแก้ไขคำขอนี้จะกลับมาที่ admin อีกครั้ง',
                });
                try {
                    await MailService.SendMailByTemplate(
                        {
                            id_workflow_status: WF.RETURN,
                            id_request: F.values.id,
                        }
                    );
                } catch (err) {
                    console.error('send email error:', err.response?.data);
                }
                navigate('/admin/requests', { replace: true });
            } else {
                showError();
            }
        } catch (err) {
            hideLoading();
            // ─── 500 แต่ status เปลี่ยนแล้ว ให้ถือว่าสำเร็จ ───
            if (err?.response?.status === 500) {
                await showSuccessDetail({
                    title: 'ส่งกลับไปยัง User แล้ว',
                    detail: 'หาก user ทำการแก้ไขคำขอนี้จะกลับมาที่ admin อีกครั้ง',
                });
                navigate('/admin/requests', { replace: true });
            } else {
                showError();
            }
        }
    };

    const handleAssignConfirm = async (messengerId) => {
        if (!messengerId) return;
        setShowAssign(false);
        const confirmed = await showConfirm({
            title: 'ยืนยันการมอบหมายงาน',
            detail: 'กด Confirm เพื่อดำเนินการต่อ',
        });
        if (!confirmed.isConfirmed) return;
         const isReassign = [WF.INCOMPLETE, WF.ASSIGN, WF.REASSIGN].includes(wf); 
        try {
            showLoading();
            const payload = { id_messenger: parseInt(messengerId), update_by: GetDataEmail() };
            if (isReassign) {
                await RequestService.ReassignMessengerRequest(F.values.id, payload);
            } else {
                await RequestService.AssignMessengerRequest(F.values.id, payload);
            }
            hideLoading();
            
            try {
                await MailService.SendMailByTemplate(
                    {
                        id_workflow_status: isReassign ? WF.REASSIGN : WF.ASSIGN,
                        id_request: F.values.id,
                    }
                );
            } catch (err) {
                console.error('send email error:', err.response?.data);
            }
            // ส่ง email แจ้ง messenger
            setShowAssign(false);
            await showSuccessDetail({
                icon: 'ri-donut-chart-fill',
                title: 'มอบหมายงานไปยัง Messenger',
                detail: 'การจัดส่งจะอัปเดทเมื่อ messenger เริ่มดำเนินการ',
            });
            window.location.href = '/admin/requests';
        } catch (err) { hideLoading(); showError(); }
    };

    const handleCloseWithCondition = async (note) => {
        try {
            showLoading();
            await RequestService.ChangeWorkflowStatus(F.values.id, {
                id_workflow_status: WF.CLOSE_COND,
                note,
                update_by: GetDataEmail(),
            });
            hideLoading();
            try {
                await MailService.SendMailByTemplate(
                    {
                        id_workflow_status: WF.CLOSE_COND,
                        id_request: F.values.id,
                    }
                );
            } catch (err) {
                console.error('send email error:', err.response?.data);
            }
            setShowDoneWithCondition(false);
            await showSuccessDetail({
                icon: 'mdi mdi-check-circle',
                title: 'เปลี่ยนสถานะเป็น Done with condition',
                detail: 'ปิดคำขอสำเร็จแล้ว',
            });
            navigate('/admin/requests', { replace: true });
        } catch (err) { hideLoading(); showError(); }
    };

    const handleComplete = async () => {
        try {
            showLoading();
            await RequestService.ChangeWorkflowStatus(F.values.id, {
                id_workflow_status: WF.CLOSE_OK,
                update_by: GetDataEmail(),
            });
            hideLoading();
            try {
                await MailService.SendMailByTemplate(
                    {
                        id_workflow_status: WF.CLOSE_OK,
                        id_request: F.values.id,
                    }
                );
            } catch (err) {
                console.error('send email error:', err.response?.data);
            }
            await showSuccessDetail({ icon: 'mdi mdi-check-circle', title: 'Completed Request' });
            navigate('/admin/requests', { replace: true });
        } catch (err) { hideLoading(); showError(); }
    };

    const toggleCard = (key) =>
        setOpenCards((prev) => ({ ...prev, [key]: !prev[key] }));

    // ─────────────────────────────────────────────────────
    // Destination card renderer
    // ─────────────────────────────────────────────────────

    /** fProps สำหรับ destination เสริม (disabled ทั้งหมด) */
    const destFProps = (dest, field) => ({
        id: `dest_${dest._id}_${field}`, name: field,
        value: dest[field] ?? '', onChange: () => {}, onBlur: () => {},
        disabled: true, className: 'form-control',
    });

    const renderDestinationCard = (dest, index) => (
        <React.Fragment key={dest._id}>
            <div className="section-label">
                Request detail (ข้อมูลคำขอ) {index + 2}/{MAX_EXTRA_DESTINATIONS + 1}
            </div>
            <AccordionCard
                title={`จุดหมายที่ ${index + 2}`}
                refNo={refNo && dest.seq ? `${refNo}-${dest.seq}` : undefined}
                isOpen={!!openCards[dest._id]}
                onToggle={() => toggleCard(dest._id)}
                badge={isMultiStopDelivery ? <StopBadge seq={dest.seq} requestDetails={requestDetails} /> : null}
            >
                <div className="form-row">
                    <div className="form-group">
                        <label className="form-label">วันที่ให้บริการ (Service Date)</label>
                        <FormDatePicker
                            value={dest.service_date ? new Date(dest.service_date) : null}
                            onChange={() => {}} disabled filterDate={isDateAvailable}
                        />
                    </div>
                    <div className="form-group">
                        <label className="form-label">จุดเริ่มต้น (Document Pickup Location)</label>
                        <select {...destFProps(dest, 'pickup_location')}>
                            <option value="">เลือกสถานที่</option>
                            {pickupLocations.map((loc) => (
                                <option key={loc.id} value={loc.id}>{loc.name_th}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="form-group mb">
                    <label className="form-label">ประเภทบริการ (Service Type)</label>
                    <select {...destFProps(dest, 'service_type')}>
                        <option value="">เลือกบริการ</option>
                        {serviceTypes.map((item) => (
                            <option key={item.id} value={item.id}>{item.name}</option>
                        ))}
                    </select>
                </div>

                {isDocumentDelivery(dest.service_type) && (
                    <div className="form-group mb">
                        <label className="form-label">เลือกแบบการรับส่งเอกสาร</label>
                        <RadioGroup name={`dest_${dest._id}_delivery_type`} options={DELIVERY_TYPE_OPTIONS} value={dest.delivery_type} />
                    </div>
                )}

                <div className="form-group mb">
                    <label className="form-label">ประเภทของเอกสาร (Request Type)</label>
                    <RadioGroup name={`dest_${dest._id}_document_secret`} options={DOCUMENT_SECRET_OPTIONS} value={dest.document_secret} />
                </div>

                <div className="form-group mb">
                    <label className="form-label">รอบการส่งมอบเอกสาร (Delivery Session)</label>
                    <select {...destFProps(dest, 'delivery_section')}>
                        <option value="">เลือกรอบ</option>
                        <option value={1}>Morning/เช้า</option>
                        <option value={2}>Afternoon/บ่าย</option>
                        <option value={3}>Urgent ด่วน</option>
                    </select>
                </div>

                <div className="form-group mb">
                    <label className="form-label">เวลาที่ต้องดำเนินการให้เสร็จ (Completion Time)</label>
                    <RadioGroup name={`dest_${dest._id}_is_complete_today`} options={COMPLETION_TIME_OPTIONS} value={dest.is_complete_today} />
                </div>

                {isDocumentDelivery(dest.service_type) && (
                    <DocumentDeliverySection
                        fProps={(field) => destFProps(dest, field)} showErr={() => null} formIsDisabled
                        buildings={buildings} provinces={provinces}
                        districts={districts} loadingDistricts={loadingDistricts}
                        onProvinceChange={() => {}}
                    />
                )}
                {String(dest.service_type) === '2' && (
                    <BankingSection
                        fProps={(field) => destFProps(dest, field)} showErr={() => null} formIsDisabled
                        providers={destProviders} loadingProviders={loadingDestProviders}
                        providerBranches={destProviderBranches} loadingBranches={loadingDestBranches}
                        onProviderChange={() => {}} onBranchChange={() => {}}
                    />
                )}
                {String(dest.service_type) === '3' && (
                    <TransactionSection
                        fProps={(field) => destFProps(dest, field)} showErr={() => null} formIsDisabled
                        transactionProviders={transactionProviders}
                        loadingTransactionProviders={loadingTransactionProviders}
                        transactionBranches={transactionBranches}
                        loadingTransactionBranches={loadingTransactionBranches}
                        onProviderChange={() => {}}
                    />
                )}
                <MessengerSection messenger={[WF.ASSIGN, WF.REASSIGN, WF.DOING, WF.INCOMPLETE, WF.COMPLETE, WF.CLOSE_OK, WF.CLOSE_COND].includes(wf) ? F.values.messenger : null} />
            </AccordionCard>
        </React.Fragment>
    );

    // ─────────────────────────────────────────────────────
    // Render
    // ─────────────────────────────────────────────────────

    return (
        <React.Fragment>
            <div className="page-wrapper">
                {/* ── Page title + ref no ── */}
                <div className="page-title">
                    รายละเอียดคำขอ
                    {refNo && (
                        <div className={`page-subtitle${[WF.CLOSE_OK, WF.CLOSE_COND].includes(wf) ? ' page-subtitle--completed' : ''}`}>
                            Ref No. {refNo}
                        </div>
                    )}
                </div>

                {/* ── Status banner ── */}
                <StatusBanner
                    wf={wf}
                    adminRemark={NOTE_FETCH_STATUSES.includes(wf) ? statusNote : F.values.admin_remark}
                    requestDetails={requestDetails}
                />

                {/* ── Form (read-only) ── */}
                <form onSubmit={(e) => e.preventDefault()}>
                    <UserInfoSection dataUser={senderInfo} />

                    <div className="section-label">
                        Request detail (ข้อมูลคำขอ) 1/{MAX_EXTRA_DESTINATIONS + 1}
                    </div>

                    <AccordionCard
                        title="จุดหมายที่ 1"
                        refNo={
                            refNo
                                ? (destinations.length > 0 ? `${refNo}-1` : refNo)
                                : undefined
                        }
                        isOpen={!!openCards.main}
                        onToggle={() => toggleCard('main')}
                        badge={isMultiStopDelivery ? <StopBadge isFirst requestDetails={requestDetails} /> : null}
                    >
                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">วันที่ให้บริการ (Service Date)</label>
                                <FormDatePicker
                                    value={F.values.service_date ? new Date(F.values.service_date) : null}
                                    onChange={() => {}} disabled filterDate={isDateAvailable}
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">จุดเริ่มต้น (Document Pickup Location)</label>
                                <select {...fProps('pickup_location')} disabled>
                                    <option value="">เลือกสถานที่</option>
                                    {buildings.map((b) => (
                                        <option key={b.id} value={b.id}>{b.name_th}</option>
                                    ))}
                                    <option value="other">อื่นๆ</option>
                                </select>
                            </div>
                        </div>

                        <div className="form-group mb">
                            <label className="form-label">ประเภทบริการ (Service Type)</label>
                            <select {...fProps('service_type')} disabled>
                                <option value="">เลือกบริการ</option>
                                {serviceTypes.map((item) => (
                                    <option key={item.id} value={item.id}>{item.name}</option>
                                ))}
                            </select>
                        </div>

                        {isDocumentDelivery(F.values.service_type) && (
                            <div className="form-group mb">
                                <label className="form-label">เลือกแบบการรับส่งเอกสาร</label>
                                <RadioGroup name="delivery_type" options={DELIVERY_TYPE_OPTIONS} value={F.values.delivery_type} />
                            </div>
                        )}

                        <div className="form-group mb">
                            <label className="form-label">ประเภทของเอกสาร (Request Type)</label>
                            <RadioGroup name="document_secret" options={DOCUMENT_SECRET_OPTIONS} value={F.values.document_secret} />
                        </div>

                        <div className="form-group mb">
                            <label className="form-label">รอบการส่งมอบเอกสาร (Delivery Session)</label>
                            <select {...fProps('delivery_section')} disabled>
                                <option value="">เลือกรอบ</option>
                                <option value={1}>Morning/เช้า</option>
                                <option value={2}>Afternoon/บ่าย</option>
                                <option value={3}>Urgent ด่วน</option>
                            </select>
                        </div>

                        <div className="form-group mb">
                            <label className="form-label">เวลาที่ต้องดำเนินการให้เสร็จ (Completion Time)</label>
                            <RadioGroup name="is_complete_today" options={COMPLETION_TIME_OPTIONS} value={F.values.is_complete_today} />
                        </div>

                        {Number(F.values.is_complete_today) === 2 && (
                            <div className="form-group mb">
                                <label className="form-label">เวลา (Time)</label>
                                <input type="time" {...fProps('time_complete_today')} disabled style={{ maxWidth: 200 }} />
                            </div>
                        )}

                        {isDocumentDelivery(F.values.service_type) && (
                            <DocumentDeliverySection
                                fProps={(field) => ({ ...fProps(field), disabled: true })}
                                showErr={() => null} formIsDisabled
                                buildings={buildings} provinces={provinces}
                                districts={districts} loadingDistricts={loadingDistricts}
                                onProvinceChange={() => {}}
                            />
                        )}
                        {String(F.values.service_type) === '2' && (
                            <BankingSection
                                fProps={(field) => ({ ...fProps(field), disabled: true })}
                                showErr={() => null} formIsDisabled
                                providers={providers} loadingProviders={loadingProviders}
                                providerBranches={providerBranches} loadingBranches={loadingBranches}
                                onProviderChange={() => {}} onBranchChange={() => {}}
                            />
                        )}
                        {String(F.values.service_type) === '3' && (
                            <TransactionSection
                                fProps={(field) => ({ ...fProps(field), disabled: true })}
                                showErr={() => null} formIsDisabled
                                transactionProviders={transactionProviders}
                                loadingTransactionProviders={loadingTransactionProviders}
                                transactionBranches={transactionBranches}
                                loadingTransactionBranches={loadingTransactionBranches}
                                onProviderChange={() => {}}
                            />
                        )}
                        <MessengerSection messenger={[WF.ASSIGN, WF.REASSIGN, WF.DOING, WF.INCOMPLETE, WF.COMPLETE, WF.CLOSE_OK, WF.CLOSE_COND].includes(wf) ? F.values.messenger : null} />
                    </AccordionCard>

                    {destinations.map((dest, index) => renderDestinationCard(dest, index))}

                    {/* ── Footer ── */}
                    <div className="form-footer">
                        <div className="footer-actions">
                            <div className="footer-left">
                                {WF_SHOW_CANCEL.includes(wf) && (
                                    <button type="button" className="btn btn-cancel" onClick={() => setShowCancel(true)}>
                                        Cancel
                                    </button>
                                )}
                            </div>
                            <div className="footer-right">
                                <button
                                    type="button"
                                    className="btn btn-secondary btn-back"
                                    onClick={() => navigate('/admin/requests', { replace: true })}
                                >
                                    <i className="ri-arrow-left-line" /> Back
                                </button>

                                {WF_SHOW_PRINT.includes(wf) && (
                                    <button type="button" className="btn btn-primary-print"
                                        onClick={handlePrint}>
                                        Print
                                    </button>
                                )}

                                {[WF.ASSIGN, WF.REASSIGN].includes(wf) && (
                                    <button type="button" className="btn btn-primary" onClick={() => setShowAssign(true)}>
                                        Re-assign
                                    </button>
                                )}

                                {[WF.CREATE, WF.USER_RESPONDED].includes(wf) && (
                                    <>
                                        <button type="button" className="btn btn-return" onClick={() => setShowReturn(true)}>Return</button>
                                        <button type="button" className="btn btn-primary" onClick={() => setShowAssign(true)}>Assign</button>
                                    </>
                                )}

                                {wf === WF.INCOMPLETE && (
                                    <div className="manage-job-dropdown" ref={manageJobRef}>
                                        <button
                                            type="button"
                                            className="btn btn-primary manage-job-btn"
                                            onClick={() => setShowManageJob((v) => !v)}
                                        >
                                            Manage Job <i className="ri-arrow-down-s-line" />
                                        </button>
                                        {showManageJob && (
                                            <div className="manage-job-menu">
                                                <p className="manage-job-menu__title">Choose an action</p>
                                                <div className="manage-job-menu__item __reassign" onClick={() => { setShowManageJob(false); setShowAssign(true); }}>
                                                    <i className="mdi mdi-clipboard-arrow-left-outline" /> Re-assign
                                                </div>
                                                <div className="manage-job-menu__item __done" onClick={() => { setShowManageJob(false); setShowDoneWithCondition(true); }}>
                                                    <i className="ri-checkbox-circle-line" /> Done with condition
                                                </div>
                                                <div className="manage-job-menu__item __cancel" onClick={() => { setShowManageJob(false); setShowCancel(true); }}>
                                                    <i className="ri-close-circle-line" /> Cancel job
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {wf === WF.COMPLETE && (
                                    <button type="button" className="btn btn-complete" onClick={handleComplete}>
                                        Complete
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </form>
            </div>

            {/* ── Modals ── */}
            {showCancel && (
                <ReturnModal
                    title="ทำการ Cancel job"
                    subtitle="คำขอจะถูกปิดงานด้วย Status cancel"
                    label="เหตุผลการยกเลิก"
                    submitLabel="Confirm"
                    showWarning={false}
                    onClose={() => setShowCancel(false)}
                    onConfirm={handleCancel}
                />
            )}
            {showReturn && (
                <ReturnModal onClose={() => setShowReturn(false)} onConfirm={handleReturn} />
            )}
            {showAssign && (
                <AssignModal
                    messengers={messengers}
                    onClose={() => setShowAssign(false)}
                    onConfirm={handleAssignConfirm}
                />
            )}
            {showDoneWithCondition && (
                <DoneWithConditionModal
                    onClose={() => setShowDoneWithCondition(false)}
                    onConfirm={handleCloseWithCondition}
                />
            )}
        </React.Fragment>
    );
};

export default AdminRequestDetail;